import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * The resting entry mode remembered for a User (MNT-21 / PREF-1). `edit` is a
 * transient, entry-scoped mode and is deliberately not part of this union — it is
 * never stored, so reopening the page never lands the User in a stale edit form.
 */
export type TimerEntryMode = 'start' | 'manual-add';

export type TimerEntryPrefsState = {
  /** The principal these prefs belong to; guards against cross-User bleed (PREF-2). */
  principalId: string | null;
  lastEntryMode: TimerEntryMode;
  lastEntryDate: string | null;
  /** The local calendar day on which `lastEntryDate` was stored — drives the PREF-4 rollover. */
  lastEntryDateRecordedOn: string | null;
};

const initialState: TimerEntryPrefsState = {
  principalId: null,
  lastEntryMode: 'start',
  lastEntryDate: null,
  lastEntryDateRecordedOn: null,
};

export const timerEntryPrefsSlice = createSlice({
  name: 'timerEntryPrefs',
  initialState,
  reducers: {
    rememberEntryMode(
      state,
      action: PayloadAction<{ principalId: string; mode: TimerEntryMode }>,
    ) {
      state.principalId = action.payload.principalId;
      state.lastEntryMode = action.payload.mode;
    },
    rememberEntryDate(
      state,
      action: PayloadAction<{ principalId: string; date: string; recordedOn: string }>,
    ) {
      state.principalId = action.payload.principalId;
      state.lastEntryDate = action.payload.date;
      state.lastEntryDateRecordedOn = action.payload.recordedOn;
    },
    resetEntryPrefs() {
      return initialState;
    },
  },
});

export const { rememberEntryMode, rememberEntryDate, resetEntryPrefs } =
  timerEntryPrefsSlice.actions;

type AppRootState = { timerEntryPrefs: TimerEntryPrefsState };

export const selectTimerEntryPrefs = (state: AppRootState) => state.timerEntryPrefs;

function belongsTo(prefs: TimerEntryPrefsState, principalId: string | null): boolean {
  return principalId !== null && prefs.principalId === principalId;
}

/**
 * The entry mode the Timer page opens in (PREF-1 / PREF-2): the User's last resting
 * mode, or `start` when nothing is remembered for this principal — a first-time User,
 * or a different User on a shared browser.
 */
export function resolveInitialEntryMode(
  prefs: TimerEntryPrefsState,
  principalId: string | null,
): TimerEntryMode {
  return belongsTo(prefs, principalId) ? prefs.lastEntryMode : 'start';
}

/**
 * The date the manual-add form opens with (PREF-4): the last-used date while the User
 * is still on the same calendar day they recorded it — so consecutive same-day entries
 * (including a deliberately retroactive date) need no re-typing — otherwise today, so a
 * stale date never carries silently into a new day.
 */
export function resolveInitialManualDate(
  prefs: TimerEntryPrefsState,
  principalId: string | null,
  today: string,
): string {
  if (!belongsTo(prefs, principalId)) return today;
  if (!prefs.lastEntryDate || prefs.lastEntryDateRecordedOn !== today) return today;
  return prefs.lastEntryDate;
}
